import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Paper, Select, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { AccountingService } from "services/accounting";
import { PortalService } from "services/portal";
import {
  IncomeStatementPanel,
  BalanceSheetPanel,
  Balance8ColPanel,
  F29FullPanel,
} from "./EngineReportsPanels";
import { MemberPicker } from "components/MemberPicker";
import BillsPending from "views/expenses/bills-pending";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const TYPES = ["ACTIVO", "PASIVO", "PATRIMONIO", "INGRESO", "GASTO"];

const fmtCLP = (n) => "$" + Math.round(n || 0).toLocaleString("es-CL");

// ─── Plan de cuentas ──────────────────────────────────────────────
function AccountsPanel({ memberId }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "ACTIVO", parentCode: "" });
  const [creating, setCreating] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await AccountingService.listAccounts(memberId);
      setAccounts(res.accounts || []);
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await AccountingService.seedAccounts(memberId);
      setFeedback({ kind: "ok", msg: `Plantilla cargada: ${res.inserted} cuentas nuevas, ${res.skipped} ya existían` });
      await load();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setSeeding(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await AccountingService.createAccount(memberId, form);
      setModalOpen(false);
      setForm({ code: "", name: "", type: "ACTIVO", parentCode: "" });
      await load();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`¿Eliminar la cuenta ${code}?`)) return;
    try {
      await AccountingService.deleteAccount(memberId, code);
      await load();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
        <Typography variant="h6">Plan de cuentas ({accounts.length})</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" disabled={seeding} onClick={handleSeed}>
            {seeding ? "Cargando..." : "Cargar plantilla Chile"}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            Nueva cuenta
          </Button>
        </Box>
      </Box>

      {feedback && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: feedback.kind === "ok" ? "success.50" : "error.50", border: 1, borderColor: feedback.kind === "ok" ? "success.light" : "error.light", color: feedback.kind === "ok" ? "success.dark" : "error.dark", fontSize: 13 }}>
          {feedback.msg}
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Padre</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              )}
              {!loading && accounts.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  Sin cuentas. Carga la plantilla para empezar.
                </TableCell></TableRow>
              )}
              {accounts.map((a) => (
                <TableRow key={a._id} hover>
                  <TableCell><code>{a.code}</code></TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell><Chip size="small" label={a.type} /></TableCell>
                  <TableCell>{a.parentCode || "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDelete(a.code)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva cuenta</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField size="small" label="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="1.1.10" fullWidth />
          <TextField size="small" label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <Select size="small" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} fullWidth>
            {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
          <TextField size="small" label="Cuenta padre (opcional)" value={form.parentCode} onChange={(e) => setForm({ ...form, parentCode: e.target.value })} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={creating} variant="contained">{creating ? "Creando..." : "Crear"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Asientos ─────────────────────────────────────────────────────
function PeriodPicker({ month, year, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Select size="small" value={month} onChange={(e) => onChange({ month: Number(e.target.value), year })}>
        {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
      </Select>
      <Select size="small" value={year} onChange={(e) => onChange({ month, year: Number(e.target.value) })}>
        {[2024, 2025, 2026, 2027].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
      </Select>
    </Box>
  );
}

function JournalPanel({ memberId }) {
  const today = new Date();
  const [period, setPeriod] = useState({ month: today.getMonth() + 1, year: today.getFullYear() });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await AccountingService.listEntries(memberId, period);
      setEntries(res.entries || []);
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId, period.month, period.year]);

  const handleGenerate = async () => {
    if (!window.confirm(`Generar asientos contables desde RCV de ${MONTHS[period.month - 1]} ${period.year}?`)) return;
    setGenerating(true);
    try {
      const res = await AccountingService.generateFromRcv(memberId, period);
      if (res.ok === false) {
        setFeedback({ kind: "error", msg: res.message });
      } else {
        setFeedback({ kind: "ok", msg: `Generados ${res.created} asientos. ${res.skipped} ya existían.` });
        await load();
      }
    } catch (err) {
      const data = err?.response?.data;
      setFeedback({ kind: "error", msg: data?.message || data?.error || err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este asiento?")) return;
    try {
      await AccountingService.deleteEntry(memberId, id);
      await load();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    }
  };

  const totals = useMemo(() => {
    let debit = 0, credit = 0;
    for (const e of entries) { debit += e.totalDebit || 0; credit += e.totalCredit || 0; }
    return { debit, credit };
  }, [entries]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6">Asientos contables ({entries.length})</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <PeriodPicker month={period.month} year={period.year} onChange={setPeriod} />
          <Button variant="contained" disabled={generating} onClick={handleGenerate}>
            {generating ? "Generando..." : "Generar desde RCV"}
          </Button>
        </Box>
      </Box>

      {feedback && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: feedback.kind === "ok" ? "success.50" : "error.50", border: 1, borderColor: feedback.kind === "ok" ? "success.light" : "error.light", color: feedback.kind === "ok" ? "success.dark" : "error.dark", fontSize: 13 }}>
          {feedback.msg}
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Líneas</TableCell>
                <TableCell align="right">Debe</TableCell>
                <TableCell align="right">Haber</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              )}
              {!loading && entries.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin asientos en este período</TableCell></TableRow>
              )}
              {entries.map((e) => (
                <React.Fragment key={e._id}>
                  <TableRow hover>
                    <TableCell>{new Date(e.date).toLocaleDateString("es-CL")}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" noWrap title={e.description}>{e.description}</Typography>
                    </TableCell>
                    <TableCell>{e.lines?.length || 0}</TableCell>
                    <TableCell align="right">{fmtCLP(e.totalDebit)}</TableCell>
                    <TableCell align="right">{fmtCLP(e.totalCredit)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.source} color={e.source === "RCV_AUTO" ? "primary" : "default"} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDelete(e._id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                  {e.lines && e.lines.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "grey.50", py: 0.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          {e.lines.map((l, i) => (
                            <Box key={i} sx={{ display: "flex", fontSize: 12, color: "text.secondary" }}>
                              <Box sx={{ flex: "0 0 90px" }}><code>{l.accountCode}</code></Box>
                              <Box sx={{ flex: 1 }}>{l.accountName} — {l.description}</Box>
                              <Box sx={{ flex: "0 0 100px", textAlign: "right" }}>{l.debit ? fmtCLP(l.debit) : ""}</Box>
                              <Box sx={{ flex: "0 0 100px", textAlign: "right" }}>{l.credit ? fmtCLP(l.credit) : ""}</Box>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {entries.length > 0 && (
                <TableRow sx={{ bgcolor: "grey.100", "& td": { fontWeight: 700 } }}>
                  <TableCell colSpan={3}>TOTALES</TableCell>
                  <TableCell align="right">{fmtCLP(totals.debit)}</TableCell>
                  <TableCell align="right">{fmtCLP(totals.credit)}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

// ─── Libro Mayor ──────────────────────────────────────────────────
function LedgerPanel({ memberId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountCode, setAccountCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const [from, setFrom] = useState(`${today.getFullYear()}-01`);
  const [to, setTo] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);

  useEffect(() => {
    if (!memberId) return;
    AccountingService.listAccounts(memberId).then((res) => setAccounts(res.accounts || []));
  }, [memberId]);

  const load = async () => {
    if (!memberId || !accountCode) return;
    setLoading(true);
    try {
      const res = await AccountingService.getLedger(memberId, { accountCode, from, to });
      setData(res);
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Select size="small" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} displayEmpty sx={{ minWidth: 280 }}>
          <MenuItem value=""><em>Selecciona una cuenta</em></MenuItem>
          {accounts.map((a) => <MenuItem key={a.code} value={a.code}>{a.code} — {a.name}</MenuItem>)}
        </Select>
        <TextField size="small" label="Desde" type="month" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Hasta" type="month" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" disabled={!accountCode || loading} onClick={load}>Consultar</Button>
      </Box>

      {data && (
        <>
          <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="subtitle2">{data.account.code} — {data.account.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Tipo: {data.account.type} · Naturaleza: {data.account.nature} · Movimientos: {data.movements.length}
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>Saldo final: {fmtCLP(data.finalBalance)}</Typography>
          </Box>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Debe</TableCell>
                    <TableCell align="right">Haber</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.movements.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin movimientos</TableCell></TableRow>
                  )}
                  {data.movements.map((m, i) => (
                    <TableRow key={i}>
                      <TableCell>{new Date(m.date).toLocaleDateString("es-CL")}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" noWrap title={`${m.description} — ${m.lineDescription}`}>
                          {m.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{m.debit ? fmtCLP(m.debit) : ""}</TableCell>
                      <TableCell align="right">{m.credit ? fmtCLP(m.credit) : ""}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCLP(m.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}

// ─── F29 ──────────────────────────────────────────────────────────
function F29Panel({ memberId }) {
  const today = new Date();
  const [period, setPeriod] = useState({ month: today.getMonth(), year: today.getFullYear() }); // mes pasado por default
  // arregla: si month es 0 → mes 12 año -1
  useEffect(() => {
    if (period.month === 0) setPeriod({ month: 12, year: period.year - 1 });
  }, []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await AccountingService.getF29(memberId, period);
      setData(res);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId, period.month, period.year]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6">Resumen F29 — {MONTHS[(period.month || 1) - 1]} {period.year}</Typography>
        <PeriodPicker month={period.month} year={period.year} onChange={setPeriod} />
      </Box>

      {loading && <CircularProgress size={20} />}

      {data && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Ventas (Débito)</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Documentos</span><b>{data.ventas.count}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Neto</span><b>{fmtCLP(data.ventas.neto)}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Exento</span><b>{fmtCLP(data.ventas.exento)}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, color: "primary.main" }}><span>IVA débito (19%)</span><b>{fmtCLP(data.ventas.iva)}</b></Box>
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1, mt: 1, display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <span>Total ventas</span><span>{fmtCLP(data.ventas.total)}</span>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Compras (Crédito)</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Documentos</span><b>{data.compras.count}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Neto</span><b>{fmtCLP(data.compras.neto)}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><span>Exento</span><b>{fmtCLP(data.compras.exento)}</b></Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, color: "primary.main" }}><span>IVA crédito (19%)</span><b>{fmtCLP(data.compras.iva)}</b></Box>
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1, mt: 1, display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <span>Total compras</span><span>{fmtCLP(data.compras.total)}</span>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, gridColumn: "1 / -1", bgcolor: "grey.100" }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Determinación del impuesto</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">IVA débito</Typography>
                <Typography variant="h6">{fmtCLP(data.iva.debito)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">IVA crédito</Typography>
                <Typography variant="h6">{fmtCLP(data.iva.credito)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">IVA a pagar</Typography>
                <Typography variant="h6" color={data.iva.aPagar > 0 ? "error.main" : "text.primary"}>
                  {fmtCLP(data.iva.aPagar)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Remanente</Typography>
                <Typography variant="h6" color={data.iva.remanente > 0 ? "success.main" : "text.primary"}>
                  {fmtCLP(data.iva.remanente)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">PPM ({(data.ppm.rate * 100).toFixed(2)}%)</Typography>
                <Typography variant="h6">{fmtCLP(data.ppm.monto)}</Typography>
              </Box>
              <Box sx={{ gridColumn: "3 / 5", textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">Total a pagar</Typography>
                <Typography variant="h5" color="error.main">{fmtCLP(data.totalAPagar)}</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

// ─── Vista raíz ───────────────────────────────────────────────────
export function AccountingView() {
  const [memberId, setMemberId] = useState(null);
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Contabilidad</Typography>
        <MemberPicker value={memberId} onChange={setMemberId} />
      </Box>

      {!memberId ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          Selecciona un cliente para ver su contabilidad
        </Paper>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label="Plan de cuentas" />
              <Tab label="Asientos" />
              <Tab label="Libro Mayor" />
              <Tab label="Gastos por aprobar" />
              <Tab label="F29 (simple)" />
              <Tab label="Estado de Resultados" />
              <Tab label="Balance General" />
              <Tab label="Balance 8 columnas" />
              <Tab label="F29 completo" />
            </Tabs>
          </Box>
          {tab === 0 && <AccountsPanel memberId={memberId} />}
          {tab === 1 && <JournalPanel memberId={memberId} />}
          {tab === 2 && <LedgerPanel memberId={memberId} />}
          {tab === 3 && <BillsPending memberId={memberId} />}
          {tab === 4 && <F29Panel memberId={memberId} />}
          {tab === 5 && <IncomeStatementPanel memberId={memberId} />}
          {tab === 6 && <BalanceSheetPanel memberId={memberId} />}
          {tab === 7 && <Balance8ColPanel memberId={memberId} />}
          {tab === 8 && <F29FullPanel memberId={memberId} />}
        </>
      )}
    </Box>
  );
}
